import React from 'react';
import ModuleBase from '@/components/general/ModuleBase';
import { CategoryModel, ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import { useRouter } from 'next/router';
import { openLink } from '@/utils/link-utils';

interface TopicsModuleProps extends ModuleBasePropsModel {
  directories?: CategoryModel[];
  innovationPlatforms?: CategoryModel[];
  demographics?: CategoryModel[];
  topics?: CategoryModel[];
}

interface TopicsModuleData extends ModuleBaseModel<TopicsModuleProps> {}

interface TopicsModuleComponentProps {
  data?: TopicsModuleData;
  [key: string]: any;
}

const TopicDirectoryGroup: React.FC<{
  group: { groupName: string; directories: CategoryModel[] };
  onItemClick: (item: CategoryModel) => (e: React.MouseEvent) => void;
}> = ({ group, onItemClick }) => (
  <div className="mx-2">
    <h4 className="my-3 font-semibold">{group.groupName}</h4>
    {group.directories.map((item: CategoryModel) =>
      item.landingPageLink?.href ? (
        <a
          key={item.id}
          href={item.landingPageLink.href}
          onClick={onItemClick(item)}
          className="block h-[30px] cursor-pointer pr-16 pl-0 text-left text-base hover:underline"
        >
          {item.categoryDisplayName}
        </a>
      ) : null,
    )}
  </div>
);

const TopicsModule: React.FC<TopicsModuleComponentProps> = ({ data, ...props }) => {
  const moduleData = data || (props as any);
  const router = useRouter();
  const [columnCount, setColumnCount] = React.useState(1);

  React.useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      setColumnCount(width >= 1280 ? 4 : width >= 880 ? 3 : width >= 640 ? 2 : 1);
    };
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  if (!moduleData?.props) return null;

  const { directories = [], innovationPlatforms = [], demographics = [], topics = [] } = moduleData.props;

  const directoryGroups = ['Lifestyle', 'Engagement', 'Product'].map((groupName) => ({
    groupName,
    directories: directories.filter((dir: CategoryModel) => dir.group === groupName),
  }));

  const handleClick = (item: CategoryModel) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (item.landingPageLink?.href) {
      openLink(router, item.landingPageLink);
    }
  };

  return (
    <ModuleBase data={moduleData}>
      <div className="container mx-auto">
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="bg-[#f5f5f5] p-2 xl:col-span-2">
            <h2 className="mx-2 mt-2 pb-5 text-lg leading-[22px] font-semibold">Directories</h2>
            <div className="grid grid-cols-1 gap-0 lg:grid-cols-2 xl:grid-cols-2">
              <div>
                <TopicDirectoryGroup group={directoryGroups[0]} onItemClick={handleClick} />
                <TopicDirectoryGroup group={directoryGroups[1]} onItemClick={handleClick} />
              </div>
              <div>
                <TopicDirectoryGroup group={directoryGroups[2]} onItemClick={handleClick} />
              </div>
            </div>
          </div>

          <div className="bg-[#f5f5f5] p-2 xl:col-span-1">
            <h2 className="mx-2 mt-2 pb-5 text-lg leading-[22px] font-semibold">Pillars of Innovation</h2>
            <div className="mx-2">
              {innovationPlatforms.map((item: CategoryModel) =>
                item.landingPageLink?.href ? (
                  <a
                    key={item.id}
                    href={item.landingPageLink.href}
                    onClick={handleClick(item)}
                    className="block h-[30px] cursor-pointer pr-16 pl-0 text-left text-base hover:underline"
                  >
                    {item.categoryDisplayName}
                  </a>
                ) : null,
              )}
            </div>
          </div>

          <div className="bg-[#f5f5f5] p-2 md:col-span-2 xl:col-span-1">
            <h2 className="mx-2 mt-2 pb-5 text-lg leading-[22px] font-semibold">Demographics</h2>
            <div className="mx-2">
              {demographics.map((item: CategoryModel) =>
                item.landingPageLink?.href ? (
                  <a
                    key={item.id}
                    href={item.landingPageLink.href}
                    onClick={handleClick(item)}
                    className="block h-[30px] cursor-pointer pr-16 pl-0 text-left text-base hover:underline"
                  >
                    {item.categoryDisplayName}
                  </a>
                ) : null,
              )}
            </div>
          </div>
        </div>

        <div className="bg-[#f5f5f5] p-2">
          <h2 className="mx-2 mt-2 pb-5 text-lg leading-[22px] font-semibold">Topics</h2>
          <div className="mx-2" style={{ columnCount }}>
            <style jsx>{`
              div > a {
                display: inline-block;
                width: 100%;
              }
            `}</style>
            {topics.map((item: CategoryModel) =>
              item.landingPageLink?.href ? (
                <a
                  key={item.id}
                  href={item.landingPageLink.href}
                  onClick={handleClick(item)}
                  className="block h-[30px] cursor-pointer pr-16 pl-0 text-left text-base hover:underline"
                >
                  {item.categoryDisplayName}
                </a>
              ) : null,
            )}
          </div>
        </div>
      </div>
    </ModuleBase>
  );
};

export default TopicsModule;
